/**
 * GoodCatchCard — spotlights the "Good Catch of the Month": the
 * highest-severity near-miss reported in the last 30 days.
 *
 * A near-miss reported (rather than swept under the rug) is the field
 * crew doing safety culture right — this card gives them public credit
 * on the dashboard without any admin lifting. Backed by
 * `/api/incidents/good-catch`, which returns `winner: null` when there
 * are no near-misses in the window; in that case the card renders
 * itself null so it doesn't clutter the dashboard.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Award, ChevronRight } from "lucide-react";

const SEV_BADGE = {
  critical: "bg-red-600 text-white",
  high:     "bg-red-800 text-red-100",
  medium:   "bg-amber-700 text-amber-100",
  low:      "bg-slate-700 text-slate-200",
};

function daysAgo(iso) {
  if (!iso) return "";
  try {
    const d = Math.floor((Date.now() - new Date(iso).getTime()) / (24 * 3600 * 1000));
    if (d <= 0) return "today";
    if (d === 1) return "1 day ago";
    return `${d} days ago`;
  } catch { return ""; }
}

export default function GoodCatchCard() {
  const [state, setState] = useState({ loading: true, winner: null });

  useEffect(() => {
    let stale = false;
    api.get("/incidents/good-catch")
      .then((r) => { if (!stale) setState({ loading: false, winner: r.data.winner }); })
      .catch(() => { if (!stale) setState({ loading: false, winner: null }); });
    return () => { stale = true; };
  }, []);

  // Silent-when-empty: no near-miss in the last 30 days means either
  // the crew is quiet or nobody's reporting — either way, no widget
  // beats a stale "N/A" placeholder.
  if (state.loading || !state.winner) return null;

  const w = state.winner;
  const sev = (w.severity || "medium").toLowerCase();
  const desc = (w.description || "").trim();

  return (
    <Link
      to={`/incidents/${w.id}`}
      className="block mb-6 group"
      data-testid="good-catch-card"
    >
      <Card className="bg-gradient-to-br from-emerald-950/60 via-slate-900 to-slate-900 border-emerald-900 hover:border-emerald-700 transition-colors">
        <CardContent className="p-5">
          <div className="flex items-start gap-4">
            <div className="p-2.5 rounded-md border border-emerald-800 bg-emerald-950/70 text-emerald-300 shrink-0">
              <Award className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] tracking-[0.3em] uppercase text-emerald-300 font-display font-bold">
                  Good Catch of the Month
                </span>
                <span
                  className={`inline-flex items-center h-4 px-1.5 rounded-full text-[9px] font-display font-bold uppercase tracking-wider ${SEV_BADGE[sev] || SEV_BADGE.medium}`}
                  data-testid="good-catch-severity"
                >
                  {sev} averted
                </span>
              </div>
              <div className="font-display font-black text-xl sm:text-2xl tracking-tight mt-1" data-testid="good-catch-reporter">
                {w.reported_by_name || "Anonymous"}
              </div>
              {desc && (
                <div className="text-sm text-slate-300 mt-1.5 line-clamp-2" data-testid="good-catch-description">
                  “{desc}”
                </div>
              )}
              <div className="text-[11px] text-slate-500 mt-2 font-mono flex items-center gap-2 flex-wrap">
                <span>{daysAgo(w.occurred_at || w.reported_at)}</span>
                {(w.site_number || w.customer) && (
                  <>
                    <span>·</span>
                    <span>{[w.site_number, w.customer].filter(Boolean).join(" · ")}</span>
                  </>
                )}
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity shrink-0 mt-1" />
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
